import { useCallback, useRef, type KeyboardEvent } from "react";
import { pdfScrollCommand, preservesPdfKeyTarget, scrollPdfViewport } from "./pdfScrollKeys";

interface OpenerAnchor {
  id: string;
  filename: boolean;
  label: string | null;
}

export interface PdfFocusReturnTarget {
  element: HTMLElement;
  anchor: OpenerAnchor | null;
}

function captureAnchor(opener: HTMLElement | null): OpenerAnchor | null {
  const row = opener?.closest<HTMLElement>("[data-document-id]");
  const id = row?.dataset.documentId;
  if (!id || !opener) return null;
  return {
    id,
    filename: opener.hasAttribute("data-row-title"),
    label: opener.getAttribute("aria-label"),
  };
}

export function capturePdfFocusReturnTarget(element: HTMLElement): PdfFocusReturnTarget {
  return { element, anchor: captureAnchor(element) };
}

function previewOpener(
  focusReturnTarget?: HTMLElement | PdfFocusReturnTarget | null
): PdfFocusReturnTarget | null {
  if (focusReturnTarget instanceof HTMLElement)
    return capturePdfFocusReturnTarget(focusReturnTarget);
  if (focusReturnTarget) return focusReturnTarget;
  const active = window.document.activeElement;
  return active instanceof HTMLElement ? capturePdfFocusReturnTarget(active) : null;
}

function remountedOpener(anchor: OpenerAnchor): HTMLElement | null {
  const row = Array.from(document.querySelectorAll<HTMLElement>("[data-document-id]")).find(
    (element) => element.dataset.documentId === anchor.id
  );
  if (anchor.filename) return row?.querySelector<HTMLElement>("[data-row-title]") ?? null;
  return (
    Array.from(row?.querySelectorAll<HTMLElement>("[aria-label]") ?? []).find(
      (element) => element.getAttribute("aria-label") === anchor.label
    ) ?? null
  );
}

function resolveOpener(opener: HTMLElement | null, anchor: OpenerAnchor | null) {
  if (opener?.isConnected) return opener;
  return anchor ? remountedOpener(anchor) : null;
}

function preservesEvent(event: KeyboardEvent<HTMLDivElement>): boolean {
  if (event.defaultPrevented || event.nativeEvent.isComposing) return true;
  if (!(event.target instanceof Element)) return true;
  return preservesPdfKeyTarget(event.target, event.key);
}

export function usePdfDialogScroll(
  isPdf: boolean,
  focusReturnTarget?: HTMLElement | PdfFocusReturnTarget | null
) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openerRowRef = useRef<OpenerAnchor | null>(null);

  const focusPreview = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog || window.document.activeElement !== dialog) return;
    const viewport = dialog.querySelector<HTMLElement>('[data-testid="pdf-scroller"]');
    viewport?.focus({ preventScroll: true });
  }, []);

  const onOpenAutoFocus = useCallback(
    (event: Event) => {
      // These controlled viewers have no Radix DialogTrigger to restore focus to.
      event.preventDefault();
      if (!isPdf) return;
      const opener = previewOpener(focusReturnTarget);
      openerRef.current = opener?.element ?? null;
      openerRowRef.current = opener?.anchor ?? null;
      dialogRef.current?.focus({ preventScroll: true });
      focusPreview();
    },
    [focusPreview, focusReturnTarget, isPdf]
  );

  const onCloseAutoFocus = useCallback((event: Event) => {
    // Virtual rows can remount while the modal is open. Restore the same control.
    const opener = resolveOpener(openerRef.current, openerRowRef.current);
    openerRef.current = null;
    openerRowRef.current = null;
    if (!opener?.isConnected) return;
    event.preventDefault();
    opener.focus({ preventScroll: true });
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isPdf || preservesEvent(event)) return;
      const command = pdfScrollCommand(event);
      if (!command) return;
      const viewport = dialogRef.current?.querySelector<HTMLElement>(
        '[data-testid="pdf-scroller"]'
      );
      // The primary and independent fallback both own a real PDF viewport.
      // Contain loading and boundary defaults without moving the background.
      event.preventDefault();
      if (viewport) scrollPdfViewport(viewport, command);
    },
    [isPdf]
  );

  return { dialogRef, onOpenAutoFocus, onCloseAutoFocus, onKeyDown, focusPreview };
}
